<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import type { PosCardCreateProps } from "@/action/addCard";
import SelectStickyType from "@/components/sticky/SelectStickyType/SelectStickyType.vue";
import SelectNamedValue from "@/components/ui/SelectNamedValue/SelectNamedValue.vue";
import type { AlmSourceId } from "@/model/baseTypes";
import type { AlmSource } from "@/model/board";
import type { ViewportCoordinate } from "@/model/coordinates";
import type { StickyType } from "@/model/stickyType";
import { useBoardStore } from "@/store/board";
import { useBoardsStore } from "@/store/boards";

const props = defineProps<{ position: ViewportCoordinate; card: PosCardCreateProps }>();

const emit = defineEmits<{
  select: [type: StickyType, almSourceId: AlmSourceId | undefined, multiCreate: boolean];
}>();

const { t } = useI18n();

const cardType = ref(props.card.type);
const almSourceId = ref(props.card.almSourceId);
const multiCreate = ref(false);
const emitSent = ref(false);

const cardTypes = useBoardStore().creatableStickyTypes;
const almSources = computed(() =>
  cardType.value
    ? useBoardsStore().stickyTypeOriginBoard(cardType.value, props.position).almSources
    : [],
);
const multiLineOptions = [
  { id: "1", name: t("multiLineStickyCreate.multi"), value: true },
  { id: "2", name: t("multiLineStickyCreate.one"), value: false },
];
type MultiLineOption = (typeof multiLineOptions)[0];

const almSourceNeeded = computed(
  () => almSources.value.length > 1 && almSourceId.value === undefined,
);
const multiLine = computed(() => props.card.text?.includes("\n"));

const finishAfterType = computed(() => !almSourceNeeded.value && !multiLine.value);
const finishAfterAlmSource = computed(() => !multiLine.value);

function setType(type: StickyType) {
  cardType.value = type;
  if (finishAfterType.value) {
    emitSelect();
  }
}

function typeSelected(e: Event) {
  if (!finishAfterType.value) {
    e.stopPropagation();
  }
}

function setAlmSource(source: AlmSource) {
  almSourceId.value = source.id;
  if (finishAfterAlmSource.value) {
    emitSelect();
  }
}

function almSourceSelected(e: Event) {
  if (!finishAfterAlmSource.value) {
    e.stopPropagation();
  }
}

function setMultiCreate(option: MultiLineOption) {
  multiCreate.value = option.value;
  emitSelect();
}

function emitSelect() {
  emit("select", cardType.value!, almSourceId.value, multiCreate.value);
  emitSent.value = true;
}
</script>

<template>
  <div v-scrollable-on-board style="width: 250px" :class="{ loading: emitSent }">
    <SelectStickyType
      v-if="!cardType"
      class="list"
      :header="$t('actions.selectStickyType')"
      :sticky-types="cardTypes"
      :disabled="emitSent"
      :model-value="cardType || cardTypes[0]"
      @update:model-value="setType($event as StickyType)"
      @click="typeSelected"
    />
    <SelectNamedValue
      v-else-if="almSourceNeeded"
      class="list"
      :header="$t('actions.selectProject')"
      :values="almSources"
      :disabled="emitSent"
      :model-value="almSources[0]"
      @update:model-value="setAlmSource($event as AlmSource)"
      @click="almSourceSelected"
    />
    <SelectNamedValue
      v-else-if="multiLine"
      class="list"
      :header="$t('multiLineStickyCreate.text')"
      :values="multiLineOptions"
      :disabled="emitSent"
      @update:model-value="setMultiCreate($event as MultiLineOption)"
    />
  </div>
</template>

<style lang="scss" scoped>
.loading {
  cursor: wait;
}

.list {
  :deep(.menu-list-items) {
    max-height: 350px;
    overflow-y: auto;
  }
}
</style>
